import {
    ConsentLevel,
    ScriptLoadResult,
    useScriptWithConsent,
} from '@news-mono/web-common'
import React from 'react'
import { LoggerContext } from '../../../diagnostics/LoggerContext'
import { ConsentFallback } from '../../../tracking/ConsentFallback/ConsentFallback'
import { TextLink } from '../../../typography/TextLink/TextLink'
import { useSectionMetaInfo } from '../../../__helpers/use-section-meta'
import { determineApiRoute, initFb } from '../helpers/facebook'
import { OEmbedResult } from '../helpers/oembed'

export interface FacebookEmbedProps {
    link: string
}

export const FacebookEmbed: React.FC<FacebookEmbedProps> = ({ link }) => {
    const { FacebookAppId, FacebookClientToken } = useSectionMetaInfo()
    const logger = React.useContext(LoggerContext)
    const [fbHtml, setFbHtml] = React.useState('')
    const [result, error] = useScriptWithConsent({
        consentMet: {
            src: 'https://connect.facebook.net/en_US/sdk.js',
        },
        consentRequiredLevel: ConsentLevel.All,
    })

    React.useEffect(() => {
        async function getFacebookOembed() {
            try {
                const fbUrl = encodeURIComponent(link)
                /**
                 * Access token generated from appid / clienttoken => {app-id}|{client-token}
                 * https://developers.facebook.com/docs/plugins/oembed
                 */
                const accessToken = `${FacebookAppId}|${FacebookClientToken}`

                // New Oembed api - omit the script as we load it via useScriptWithConsent
                const graphRequest = await fetch(
                    `https://graph.facebook.com/v8.0/${determineApiRoute(
                        link,
                    )}?omitscript=true&url=${fbUrl}&access_token=${accessToken}`,
                )

                const instaResp = (await graphRequest.json()) as OEmbedResult

                const html = instaResp.html

                if (html) {
                    setFbHtml(html)
                    initFb(FacebookAppId)
                }
            } catch (error) {
                logger.debug({ error, link }, 'Error loading facebook post')
            }
        }

        if (result === ScriptLoadResult.Success) {
            getFacebookOembed()
        }
    }, [result, error, FacebookClientToken, FacebookAppId, link, logger])

    if (result === ScriptLoadResult.NotConsented) {
        return <ConsentFallback />
    }

    if (result === ScriptLoadResult.Failed) {
        return (
            <TextLink href={link} openInNewWindow={true}>
                View Facebook Embed
            </TextLink>
        )
    }

    if (!fbHtml || result === ScriptLoadResult.Loading) {
        return (
            <TextLink href={link} openInNewWindow={true}>
                Loading Facebook Embed
            </TextLink>
        )
    }

    if (fbHtml) {
        return (
            <div
                dangerouslySetInnerHTML={{
                    __html: fbHtml,
                }}
            />
        )
    }

    return null
}

export default FacebookEmbed
